import { DEFAULT_LOCALE } from '@solinkcorp/vd-common';
import { useCallback, useMemo } from 'react';
import {
  type DataExplorerVerifeyePreferences,
  type TimeFormat,
  type UserPreferences,
  type Verifeye4Preferences,
} from './callHome/callHomeAuthTypes';
import { useAuthenticationState } from './internal/AuthenticationContext';
import { updatePreferences } from './state/authenticationActions';

const DEFAULT_360_VIEW_MULTIPLAY = 'dewarped';
const DEFAULT_HEATMAP_WINDOW = 1;

export const DEFAULT_DOWNLOAD_TYPE = 'mp4';

export const EVENT_LIST_LAYOUT_TYPES = {
  WATERFALL: 'WATERFALL',
  PEEK: 'PEEK',
} as const;
export type EventListLayoutType = 'WATERFALL' | 'PEEK';

export type SetUserPreferences = (preferences: Partial<UserPreferences>) => Promise<void>;

export interface UserPreferencesWithDefaults extends UserPreferences {
  default360ViewMultiplay: NonNullable<UserPreferences['default360ViewMultiplay']>;
  defaultHeatmapWindow: NonNullable<UserPreferences['defaultHeatmapWindow']>;
  defaultVideoFormat: NonNullable<UserPreferences['defaultVideoFormat']>;
  eventListLayoutType: NonNullable<UserPreferences['eventListLayoutType']>;
  timeFormat?: TimeFormat;
  defaultToHD: boolean;
  show360DewarpToggle: boolean;
  showDateOnVideo: boolean;
  showEventProgressInVideoPlayer: boolean;
  widgetRefreshEnabled: boolean;
  verifEyeDarkMode: boolean;
  verifeye4: Required<Verifeye4Preferences>;
  dataExplorerVerifeye: Required<DataExplorerVerifeyePreferences>;
}

const DEFAULT_VERIFEYE4_PREFERENCES: Required<Verifeye4Preferences> = {
  sortBy: 'oldest',
  eventTypes: [],
  limitResults: 50,
  siteLabels: [],
  alarmZones: [],
};

const DEFAULT_DATA_EXPLORER_VERIFEYE_PREFERENCES: Required<DataExplorerVerifeyePreferences> = {
  sortBy: 'newest',
  closedEmployee: '',
  closedConclusion: 'all',
  closedOutcome: '',
};

export const defaultUserPreferences: UserPreferencesWithDefaults = {
  locale: DEFAULT_LOCALE,
  timeFormat: undefined,
  default360ViewMultiplay: DEFAULT_360_VIEW_MULTIPLAY,
  defaultHeatmapWindow: DEFAULT_HEATMAP_WINDOW,
  defaultVideoFormat: DEFAULT_DOWNLOAD_TYPE,
  eventListLayoutType: EVENT_LIST_LAYOUT_TYPES.PEEK,
  defaultToHD: false,
  show360DewarpToggle: false,
  showDateOnVideo: false,
  showEventProgressInVideoPlayer: false,
  widgetRefreshEnabled: false,
  verifEyeDarkMode: true,
  verifeye4: DEFAULT_VERIFEYE4_PREFERENCES,
  dataExplorerVerifeye: DEFAULT_DATA_EXPLORER_VERIFEYE_PREFERENCES,
};

/**
 * Returns the current user preferences, and a function for updating user preferences.
 */
export function useUserPreferences(): [UserPreferencesWithDefaults, SetUserPreferences] {
  const [authState, authDispatch] = useAuthenticationState();

  const original = authState.userSession?.profile.preferences;

  const preferences: UserPreferencesWithDefaults = useMemo(
    () => ({
      ...original,
      locale: original?.locale ?? DEFAULT_LOCALE,
      timeFormat: original?.timeFormat || undefined,
      default360ViewMultiplay: original?.default360ViewMultiplay ?? DEFAULT_360_VIEW_MULTIPLAY,
      defaultHeatmapWindow: original?.defaultHeatmapWindow ?? DEFAULT_HEATMAP_WINDOW,
      defaultVideoFormat: original?.defaultVideoFormat ?? DEFAULT_DOWNLOAD_TYPE,
      eventListLayoutType: original?.eventListLayoutType ?? EVENT_LIST_LAYOUT_TYPES.PEEK,
      defaultToHD: original?.defaultToHD ?? false,
      show360DewarpToggle: original?.show360DewarpToggle ?? false,
      showDateOnVideo: original?.showDateOnVideo ?? false,
      showEventProgressInVideoPlayer: original?.showEventProgressInVideoPlayer ?? false,
      widgetRefreshEnabled: original?.widgetRefreshEnabled ?? false,
      verifEyeDarkMode: original?.verifEyeDarkMode ?? true,
      verifeye4: {
        sortBy: original?.verifeye4?.sortBy ?? DEFAULT_VERIFEYE4_PREFERENCES.sortBy,
        eventTypes: original?.verifeye4?.eventTypes ?? DEFAULT_VERIFEYE4_PREFERENCES.eventTypes,
        limitResults:
          original?.verifeye4?.limitResults ?? DEFAULT_VERIFEYE4_PREFERENCES.limitResults,
        siteLabels: original?.verifeye4?.siteLabels ?? DEFAULT_VERIFEYE4_PREFERENCES.siteLabels,
        alarmZones: original?.verifeye4?.alarmZones ?? DEFAULT_VERIFEYE4_PREFERENCES.alarmZones,
      },
      dataExplorerVerifeye: {
        sortBy:
          original?.dataExplorerVerifeye?.sortBy ??
          DEFAULT_DATA_EXPLORER_VERIFEYE_PREFERENCES.sortBy,
        closedEmployee:
          original?.dataExplorerVerifeye?.closedEmployee ??
          DEFAULT_DATA_EXPLORER_VERIFEYE_PREFERENCES.closedEmployee,
        closedConclusion:
          original?.dataExplorerVerifeye?.closedConclusion ??
          DEFAULT_DATA_EXPLORER_VERIFEYE_PREFERENCES.closedConclusion,
        closedOutcome:
          original?.dataExplorerVerifeye?.closedOutcome ??
          DEFAULT_DATA_EXPLORER_VERIFEYE_PREFERENCES.closedOutcome,
      },
    }),
    [original],
  );

  const setPreferences = useCallback(
    (preferences: Partial<UserPreferences>): Promise<void> => {
      // Optimistically update the preference.

      if (!authState.userSession) {
        throw new Error('Cannot update preferences when no user is logged in');
      }

      return updatePreferences(
        authDispatch,
        authState.userSession.accessToken,
        authState.userSession.profile.preferences,
        preferences,
      );
    },
    [authDispatch, authState.userSession],
  );

  return [preferences, setPreferences];
}
